/** Flow 3 — Collecte des objectifs (simulation, no backend) */

export const FLOW3_TITLE = "Flow 3 — Collecte des objectifs";

export const FLOW3_STEPS = [
  "L'élève mentionne un devoir ou examen",
  "L'assistant pose des questions simples",
  "Collecte matière / type / échéance",
  "Collecte durée estimée ou étapes",
  "Confirmation de l'objectif créé",
];

export const FLOW3_CANON = {
  mention: "J'ai un contrôle de maths sur les fractions.",
  subject: "Maths",
  type: "Contrôle / examen",
  due: "Lundi 20 octobre",
  duration: "2 h",
  stepsText:
    "Oui, je dois revoir le cours et faire les exercices du chapitre 3.",
};

export const SUBJECT_OPTIONS = [
  "Maths",
  "Français",
  "Sciences",
  "Histoire",
  "Autre",
];

export const TYPE_OPTIONS = [
  "Devoir maison",
  "Contrôle / examen",
  "Projet",
  "Exposé",
  "Autre",
];

export const DURATION_OPTIONS = [
  "30 min",
  "1 h",
  "2 h",
  "3 h",
  "Je ne sais pas encore",
];

export const AFTER_MENTION =
  "Parfait ! Je vais te poser quelques questions pour bien comprendre et créer ton objectif.";

export const SUBJECT_PROMPT =
  "Très bien !\nJe te pose quelques questions simples pour créer ton objectif.\n\n❓ Quelle matière concernée ?";

export const DURATION_PROMPT =
  "Super ! Maintenant, combien de temps penses-tu devoir y consacrer ?";

export const STEPS_PROMPT =
  "Tu peux aussi me dire s'il y a des étapes spécifiques à prévoir (facultatif).";

export const DUE_PROMPT = "Et quelle est la date d'échéance ?";

export const CONFIRM_INTRO = "C'est parfait ! 🎉\nJ'ai créé ton objectif :";

export const CONFIRM_OUTRO =
  "Tu le retrouveras dans ton plan.\nJe t'enverrai des rappels pour t'avancer progressivement ! 💪";

export function emptyObjective() {
  return {
    mention: "",
    subject: "",
    type: "",
    due: "",
    duration: "",
    steps: [],
  };
}

export function flow3Intro(studentName) {
  return `👋 Salut ${studentName} !\nDis-moi simplement quel devoir, examen ou projet tu veux préparer.\n\nJe vais t'aider à en faire un objectif clair.`;
}

export const RHYTHM_MESSAGE =
  "Tu peux me le dire ici, ou prendre une photo de tes échéances.\n\nJe ferai un suivi chaque jour, pour chaque tâche. Le mieux est de préparer ta semaine le jeudi, avant la fin de semaine.";

export function typePrompt(subject) {
  return `Parfait, c'est bien en ${subject} !\nQuel type de travail s'agit-il ?`;
}

export function durationLabel(value) {
  if (!value || value === "Je ne sais pas encore") return "À préciser";
  return value;
}

export function durationMinutes(value) {
  if (value === "30 min") return 30;
  if (value === "1 h") return 60;
  if (value === "2 h") return 120;
  if (value === "3 h") return 180;
  return 0;
}

export function parseSteps(text) {
  if (!text) return [];
  const trimmed = text.trim();
  if (/^(non|aucune|pas d)/i.test(trimmed)) return [];
  const body = trimmed
    .replace(/^oui[,.]?\s*/i, "")
    .replace(/^je dois\s+/i, "")
    .replace(/\.$/, "");
  return body
    .split(/\s+et\s+/i)
    .map((part) => part.trim())
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1));
}

export function flow3DisplayIndex(flowStep) {
  if (flowStep <= 0) return 0;
  if (flowStep === 1) return 1;
  if (flowStep <= 3) return 2;
  if (flowStep <= 5) return 3;
  return 4;
}

function slug(label) {
  return label
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/(^-|-$)/g, "");
}

function choiceReplies(prefix, options, primaryLabel) {
  return options.map((label) => ({
    id: `${prefix}${slug(label)}`,
    label,
    primary: label === primaryLabel,
  }));
}

export function subjectReplies() {
  return choiceReplies("f3-subject-", SUBJECT_OPTIONS, FLOW3_CANON.subject);
}

export function typeReplies() {
  return choiceReplies("f3-type-", TYPE_OPTIONS, FLOW3_CANON.type);
}

export function durationReplies() {
  return choiceReplies("f3-duration-", DURATION_OPTIONS, FLOW3_CANON.duration);
}

export function mentionReply() {
  return [
    { id: "f3-mention", label: FLOW3_CANON.mention, primary: true },
    { id: "f3-photo", label: "Photo d'une échéance", primary: false },
  ];
}

export function dueReply() {
  return [{ id: "f3-due", label: FLOW3_CANON.due, primary: true }];
}

export function stepsReplies() {
  return [
    { id: "f3-steps", label: FLOW3_CANON.stepsText, primary: true },
    { id: "f3-nosteps", label: "Pas d'étapes précises", primary: false },
  ];
}

export function labelForReply(id, options, prefix) {
  return options.find((label) => `${prefix}${slug(label)}` === id) || "";
}

function result(messages, quickReplies, flowStep, objective, planReady = false) {
  const status = FLOW3_STEPS[flow3DisplayIndex(flowStep)];
  return { messages, quickReplies, flowStep, status, objective, planReady };
}

/** Instant history for a sommaire step (display index 0–4). */
export function buildFlow3Snapshot(displayIndex, studentName) {
  const step = Math.min(Math.max(displayIndex, 0), FLOW3_STEPS.length - 1);
  const canon = FLOW3_CANON;
  const objective = emptyObjective();
  const messages = [
    { sender: "agent", messageKey: "f3.intro", text: flow3Intro(studentName) },
    { sender: "agent", messageKey: "f3.rhythm", text: RHYTHM_MESSAGE },
  ];

  if (step === 0) {
    return result(messages, mentionReply(), 0, objective);
  }

  objective.mention = canon.mention;
  messages.push({ sender: "user", text: canon.mention });
  messages.push({ sender: "agent", messageKey: "f3.after", text: AFTER_MENTION });
  messages.push({ sender: "agent", messageKey: "f3.subject", text: SUBJECT_PROMPT });
  if (step === 1) {
    return result(messages, subjectReplies(), 1, objective);
  }

  objective.subject = canon.subject;
  messages.push({ sender: "user", text: canon.subject });
  messages.push({
    sender: "agent",
    messageKey: "f3.type",
    text: typePrompt(canon.subject),
  });
  if (step === 2) {
    return result(messages, typeReplies(), 2, objective);
  }

  objective.type = canon.type;
  objective.due = canon.due;
  messages.push({ sender: "user", text: canon.type });
  messages.push({ sender: "agent", messageKey: "f3.due", text: DUE_PROMPT });
  messages.push({ sender: "user", text: canon.due });
  messages.push({ sender: "agent", messageKey: "f3.duration", text: DURATION_PROMPT });
  if (step === 3) {
    return result(messages, durationReplies(), 4, objective);
  }

  objective.duration = canon.duration;
  objective.steps = parseSteps(canon.stepsText);
  messages.push({ sender: "user", text: canon.duration });
  messages.push({ sender: "agent", messageKey: "f3.steps", text: STEPS_PROMPT });
  messages.push({ sender: "user", text: canon.stepsText });
  messages.push({ sender: "agent", messageKey: "f3.confirmIntro", text: CONFIRM_INTRO });
  messages.push({
    sender: "agent",
    kind: "objective",
    objective: { ...objective, steps: [...objective.steps] },
  });
  messages.push({ sender: "agent", messageKey: "f3.confirmOutro", text: CONFIRM_OUTRO });
  return result(messages, [], 6, objective, true);
}
